import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaLinkedin, FaGithub, FaEnvelope, FaArrowRight } from "react-icons/fa";
import { SiLeetcode } from "react-icons/si";
import { sendContactMessage } from "../services/api";

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } } };

const Contact = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await sendContactMessage(formData);
      setSent(true);
      setFormData({ name: "", email: "", message: "" });
      setTimeout(() => setSent(false), 4000);
    } catch (error) {
      alert("Failed to send message. Try again later.");
    } finally {
      setSending(false);
    }
  };

  const links = [
    { icon: <FaEnvelope />, label: "shivanshgaur28@gmail.com", href: "mailto:shivanshgaur28@gmail.com", mono: "mail", color: "group-hover:text-red-400" },
    { icon: <FaGithub />, label: "Shivansh-Gaur2", href: "https://github.com/Shivansh-Gaur2/", mono: "git", color: "group-hover:text-white" },
    { icon: <FaLinkedin />, label: "shivansh-gaur", href: "https://www.linkedin.com/in/shivansh-gaur/", mono: "in", color: "group-hover:text-blue-400" },
    { icon: <SiLeetcode />, label: "shivanshgaur28", href: "https://leetcode.com/u/shivanshgaur28/", mono: "lc", color: "group-hover:text-yellow-400" },
  ];

  return (
    <div className="relative text-white max-w-5xl mx-auto px-6 py-16 overflow-hidden min-h-[80vh] flex flex-col justify-center">
      <div className="noise-overlay"></div>

      {/* Ambient glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px] animate-drift"></div>
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-emerald-600/8 rounded-full blur-[150px] animate-drift-reverse"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-violet-500/5 rounded-full blur-[100px] animate-breathe"></div>
      </div>

      {/* Giant decorative watermark */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none select-none">
        <span className="text-[20rem] font-black text-white/[0.015] leading-none tracking-tighter">Hi</span>
      </div>

      <div className="relative z-10">
        {/* Section header */}
        <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <p className="text-[10px] text-gray-600 uppercase tracking-[0.4em] font-mono mb-2">06 / Contact</p>
          <h2 className="text-4xl md:text-5xl font-black tracking-tight">Let's build<br /><span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-emerald-400">something</span></h2>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {/* Left side — statement + links */}
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="md:col-span-5">
            <motion.p variants={fadeUp} className="text-gray-400 text-base leading-[1.8] font-light mb-10 max-w-md">
              Got a role that needs a <span className="text-white font-medium">systems thinker</span>? Building something at <span className="text-white font-medium">scale</span>? Or just want to talk distributed systems over coffee? I'm all ears.
            </motion.p>

            {/* Contact links */}
            <motion.div variants={fadeUp} className="space-y-0 border-t border-gray-800/60">
              {links.map((link, i) => (
                <a key={i} href={link.href} target={link.href.startsWith("http") ? "_blank" : undefined} rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="group flex items-center gap-4 py-4 border-b border-gray-800/60 hover:border-gray-600/60 hover:bg-white/[0.02] hover:pl-2 transition-all duration-300 rounded-r-lg"
                >
                  <span className="font-mono text-[10px] text-gray-700 w-8">{link.mono}</span>
                  <span className={`text-gray-600 ${link.color} transition-colors duration-300 text-lg`}>{link.icon}</span>
                  <span className="text-sm text-gray-400 group-hover:text-white transition-colors duration-300 flex-1 font-medium">{link.label}</span>
                  <FaArrowRight className="text-[10px] text-gray-800 group-hover:text-gray-500 group-hover:translate-x-1 transition-all duration-300" />
                </a>
              ))}
            </motion.div>

            {/* Status */}
            <motion.div variants={fadeUp} className="mt-8 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
              <span className="text-xs font-mono text-gray-600">currently responding within 24h</span>
            </motion.div>
          </motion.div>

          {/* Right side — form */}
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="md:col-span-7">
            <motion.div variants={fadeUp}>
              {/* Terminal-style form header */}
              <div className="flex items-center gap-2 mb-6">
                <span className="w-3 h-3 rounded-full bg-red-500/60"></span>
                <span className="w-3 h-3 rounded-full bg-yellow-500/60"></span>
                <span className="w-3 h-3 rounded-full bg-green-500/60"></span>
                <span className="ml-3 text-xs text-gray-700 font-mono">new_message.sh</span>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-[10px] text-gray-600 uppercase tracking-[0.2em] font-mono mb-2">Name</label>
                    <input
                      type="text" name="name" value={formData.name} onChange={handleChange} required
                      className="w-full px-4 py-3 bg-white/[0.02] text-white text-sm border border-gray-800/60 rounded-lg focus:border-blue-400/50 focus:bg-white/[0.04] focus:shadow-lg focus:shadow-blue-500/5 outline-none transition-all duration-300 placeholder-gray-700 font-light"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-600 uppercase tracking-[0.2em] font-mono mb-2">Email</label>
                    <input
                      type="email" name="email" value={formData.email} onChange={handleChange} required
                      className="w-full px-4 py-3 bg-white/[0.02] text-white text-sm border border-gray-800/60 rounded-lg focus:border-blue-400/50 focus:bg-white/[0.04] focus:shadow-lg focus:shadow-blue-500/5 outline-none transition-all duration-300 placeholder-gray-700 font-light"
                      placeholder="you@company.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] text-gray-600 uppercase tracking-[0.2em] font-mono mb-2">Message</label>
                  <textarea
                    name="message" value={formData.message} onChange={handleChange} required rows="6"
                    className="w-full px-4 py-3 bg-white/[0.02] text-white text-sm border border-gray-800/60 rounded-lg focus:border-blue-400/50 focus:bg-white/[0.04] focus:shadow-lg focus:shadow-blue-500/5 outline-none transition-all duration-300 placeholder-gray-700 resize-none font-light"
                    placeholder="Tell me about the role, the project, or just say hello..."
                  />
                </div>

                <div className="flex items-center gap-4">
                  <button
                    type="submit" disabled={sending}
                    className="group relative px-8 py-3.5 rounded-lg text-sm font-medium overflow-hidden bg-gradient-to-r from-blue-600/20 to-violet-600/20 border border-blue-500/25 hover:border-blue-400/50 hover:from-blue-600/30 hover:to-violet-600/30 hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-500 disabled:opacity-40"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-violet-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></span>
                    <span className="relative flex items-center gap-2">
                      {sending ? (
                        <>
                          <span className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin"></span>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send message
                          <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform duration-300" />
                        </>
                      )}
                    </span>
                  </button>

                  {sent && (
                    <motion.span initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="text-xs font-mono text-emerald-400">
                      &#10003; message sent successfully
                    </motion.span>
                  )}
                </div>
              </form>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
